import React from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { DocumentService } from '../../../../../services/DocumentService';
import i18n from '../../../../../plugins/i18n/i18n';
import { useLocalStorage } from '../../../../../hooks/useLocalStorage';
import { errorToast, successToast } from '../../../../../utils/toasts';
import { errorMessageAdapter } from '../../../../../adapters';
import { AxiosError, AxiosInstance } from 'axios';
import { useDialogApprovalDocumentStore } from '../../store/dialogs.store';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../../../../services/hooks/apiHook';

const DialogApproved = ({ shareholderSlug, documentSlug, onDocumentApproved, onClose }: any) => {
  const { t } = useTranslation();
  const [token] = useLocalStorage('access-token', null);
  const { open: openApproved, setOpen: setOpenApproved }: any = useDialogApprovalDocumentStore();
  const [loading, setLoading] = React.useState(false);
  const api: AxiosInstance = useApi();

  const aproveDocument = async () => {
    try {
      setLoading(true);
      await DocumentService.approveDocument(
        api,
        {
          shareholder_slug: shareholderSlug,
          document_slug: documentSlug
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Language': i18n.language.toLowerCase()
          }
        }
      );
      successToast(t('others.othDocumentApproved') || 'Documento aprovado com sucesso!');

      onDocumentApproved('approved');
      setOpenApproved(false);
      if (onClose) {
        onClose();
      }
    } catch (e) {
      errorToast(errorMessageAdapter(e as AxiosError));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog.Root open={openApproved} onOpenChange={setOpenApproved}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm data-[state=open]:animate-overlayShow" />
        <AlertDialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[450px] translate-x-[-50%] translate-y-[-50%] rounded-xl bg-white shadow-2xl focus:outline-none data-[state=open]:animate-contentShow overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-green-200">
            <AlertDialog.Title className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t('others.othModalApproved')}
            </AlertDialog.Title>
          </div>

          {/* Content */}
          <div className="p-6">
            <AlertDialog.Description className="text-center">
              <div className="flex flex-col items-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3zM7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3" />
                  </svg>
                </div>
                <p className="text-lg text-gray-700 font-medium">
                  {t('others.othAreYouSureApprove')}
                </p>
              </div>
            </AlertDialog.Description>

            {/* Actions */}
            <div className="flex justify-center gap-4">
              <AlertDialog.Cancel asChild>
                <button
                  onClick={() => setOpenApproved(false)}
                  className="flex items-center justify-center px-6 py-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-gray-700 font-medium transition-colors"
                  disabled={loading}
                >
                  {t('others.othCancel')}
                </button>
              </AlertDialog.Cancel>
              
              <button
                onClick={aproveDocument}
                disabled={loading}
                className="flex items-center justify-center px-6 py-3 bg-green-600 hover:bg-green-700 rounded-lg text-white font-medium transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="w-5 h-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('others.othApprove')}</span>
                  </div>
                )}
              </button>
            </div>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
};

export default DialogApproved;
